/**
 * Feldwerte eines Etiketts als Text (Story 27.5): eine Formatierung für die Vorschau im Editor (27.5) und den Druck
 * (27.6). Eingabe ist der Wert einer Listen-Spalte in der Form der Listen-Schnittstelle (Datum `YYYY-MM-DD`, Betrag
 * `{ amountMinor, currency }`, Zahl, Ja/Nein, Auswahlwert) und nur die Struktur der Spalte (`type`, `options`) – kein
 * Import aus `reporting`. Geld exakt über `formatMoneyDe` (kleinste Einheit als BigInt, Währung aus dem Wert, nie
 * Gleitkomma). Rein, browser-sicher: keine Uhr, kein Netz.
 */
import { formatMoneyDe } from '@till-erp/platform/currencies';

/** Struktur einer Spalte, soweit die Formatierung sie braucht (Typ der Listen-Spalte, Auswahlwerte mit Beschriftung). */
export interface LabelFieldColumn {
  type: string;
  options?: readonly { value: string; label: string }[] | undefined;
}

const NUMBER = new Intl.NumberFormat('de-DE', { maximumFractionDigits: 4 });
/** Kalendertag in Europe/Berlin (Zeitpunkte kommen als ISO in UTC). */
const BERLIN_DAY = new Intl.DateTimeFormat('de-DE', { timeZone: 'Europe/Berlin', day: '2-digit', month: '2-digit', year: 'numeric' });
/** Minuszeichen wie `formatMoneyDe` (U+2212), für Zahlen aus beiden Wegen gleich. */
const MINUS = '\u2212';

/** Zahl deutsch (Komma, Tausenderpunkt); Dezimaltext (exakte Liste) ohne Umweg über eine JavaScript-Zahl. */
function formatNumber(value: unknown): string | null {
  if (typeof value === 'number') return Number.isFinite(value) ? NUMBER.format(value).replace('-', MINUS) : null;
  if (typeof value === 'bigint') return NUMBER.format(value).replace('-', MINUS);
  if (typeof value === 'string' && /^-?\d+(\.\d+)?$/.test(value.trim())) {
    const [int, frac] = value.trim().split('.');
    const negative = int!.startsWith('-');
    const digits = negative ? int!.slice(1) : int!;
    const grouped = digits.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    const fraction = frac?.replace(/0+$/, '') ?? '';
    return `${negative ? MINUS : ''}${grouped}${fraction ? `,${fraction}` : ''}`;
  }
  return null;
}

/** `YYYY-MM-DD` → `TT.MM.JJJJ`. */
function formatDate(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  return m ? `${m[3]}.${m[2]}.${m[1]}` : null;
}

/** Zeitpunkt (ISO, UTC) → Tag in Europe/Berlin `TT.MM.JJJJ`; nur ein Datum ohne Uhrzeit wie `formatDate`. */
function formatDateTime(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return formatDate(value);
  const ms = isoToEpochMs(value);
  return ms === null ? null : BERLIN_DAY.format(ms);
}

/** Tage seit 1970-01-01 für ein Kalenderdatum (proleptisch gregorianisch, H. Hinnant „days_from_civil“). */
function daysFromCivil(y: number, m: number, d: number): number {
  const yy = m <= 2 ? y - 1 : y;
  const era = Math.floor(yy / 400);
  const yoe = yy - era * 400;
  const doy = Math.floor((153 * (m + (m > 2 ? -3 : 9)) + 2) / 5) + d - 1;
  const doe = yoe * 365 + Math.floor(yoe / 4) - Math.floor(yoe / 100) + doy;
  return era * 146097 + doe - 719468;
}

/** ISO-Zeitpunkt mit `Z` oder ±HH:MM → Millisekunden seit 1970 (rein, ohne Uhr); sonst `null`. */
function isoToEpochMs(value: string): number | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2})(?:\.\d+)?)?(Z|([+-])(\d{2}):?(\d{2}))$/.exec(value);
  if (!m) return null;
  const [, y, mo, d, h, mi, sec, , sign, oh, om] = m;
  const offsetMin = sign ? (sign === '-' ? -1 : 1) * (Number(oh) * 60 + Number(om)) : 0;
  const minutes = daysFromCivil(Number(y), Number(mo), Number(d)) * 1440 + Number(h) * 60 + Number(mi) - offsetMin;
  return (minutes * 60 + Number(sec ?? 0)) * 1000;
}

function optionLabel(column: LabelFieldColumn, value: unknown): string {
  const raw = String(value);
  return column.options?.find((o) => o.value === raw)?.label ?? raw;
}

/**
 * Wert einer Spalte als Text fürs Etikett; `null` = leer (nichts gespeichert oder nicht darstellbar, z. B. Bild,
 * Signale). Zeitpunkte zeigen ihren Kalendertag in Europe/Berlin.
 */
export function formatLabelFieldValue(column: LabelFieldColumn, value: unknown): string | null {
  if (value === null || value === undefined || value === '') return null;
  switch (column.type) {
    case 'money': {
      if (typeof value !== 'object') return null;
      const { amountMinor, currency } = value as { amountMinor?: unknown; currency?: unknown };
      if ((typeof amountMinor !== 'string' && typeof amountMinor !== 'number' && typeof amountMinor !== 'bigint') || typeof currency !== 'string' || !currency) return null;
      // Zahl nur als sichere Ganzzahl (größere Beträge wären schon ungenau), Text nur aus Ziffern (Story 27.6, Hinweis 27.5).
      if (typeof amountMinor === 'number' && !Number.isSafeInteger(amountMinor)) return null;
      if (typeof amountMinor === 'string' && !/^-?\d+$/.test(amountMinor)) return null;
      try {
        return formatMoneyDe(BigInt(amountMinor), currency);
      } catch {
        return null;
      }
    }
    case 'number':
    case 'quantity':
      return formatNumber(value);
    case 'percent': {
      const n = formatNumber(value);
      return n === null ? null : `${n} %`;
    }
    case 'date':
      return formatDate(value);
    case 'datetime':
      return formatDateTime(value);
    case 'boolean':
      return value === true ? 'Ja' : value === false ? 'Nein' : null;
    case 'enum':
    case 'status':
      return optionLabel(column, value);
    case 'multi_enum': {
      const list = Array.isArray(value) ? value : [value];
      const text = list.filter((v) => v !== null && v !== undefined && v !== '').map((v) => optionLabel(column, v));
      return text.length > 0 ? text.join(', ') : null;
    }
    case 'image':
    case 'signals':
      return null;
    default:
      return typeof value === 'object' ? null : String(value);
  }
}
